// CommonTable 的文档：http://databrain-docs.intlgame.com/blog-5.html
/* eslint-disable arrow-body-style */
import { v4 as uuidv4 } from 'uuid';
import { CommonBaseTable } from '@home/components/CommonBaseTable';
import CommonBubble from '@home/components/CommonBubble';
import { SortHeaderCellProps, SortItem } from 'ali-react-table';
import classNames from 'classnames';
import React, {
  CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'umi';
import MetricsCardWrap from '../MetricsCardWrap';
import MetricAndUpDown from '../MetricAndUpDown';
import Render from '../PageContainer/Render';
import { PercentageCell } from './components/CommonTableCell/PercentageCell';
import CommonTableHeader from './components/CommonTableHeader';
import './index.scss';
import '../MetricsCardDod/index.scss';
import { IColumn, ICommonTableProps, ICommonTableRef } from './type';
import { Cell } from './components/CommonTableCell/Cell';
import { useLocal, useSelectorMap } from '../../utils/customHook';
import { ICommonBaseTableRef } from '@home/components/CommonBaseTable/_type';
import { MarkdownLinkCell } from './components/CommonTableCell/MarkdownLinkCell';
import { DateCell } from './components/CommonTableCell/DateCell';
import { GameCell } from './components/CommonTableCell/GameCell';
import { StudioCell } from './components/CommonTableCell/StudioCell';
import { SparklineCell } from './components/CommonTableCell/SparklineCell';
import { CategoryCell } from './components/CommonTableCell/CategoryCell';
import numeral from 'numeral';
import { getName } from '@home/utils/common';
import { PAGE_SIZE_OPTIONS } from './config';
import {
  convertSortFormat,
  defaultCompareFn,
  expandColumnsForDownload,
  filterComponentMetricsByTableMetric,
  metricsToColumns,
} from './utils';
import { resolveColumns } from './utils/resolveColumns';
import { useForegroundPaging } from './hooks/useForegroundPaging';
import { useBackendPaging } from './hooks/useBackendPaging';
import { useVisibleOperation } from './hooks/useVisibleOperation';
import dataCommonFormat from '@home/utils/dbDataFormat/dataCommonFormat';
import { NumberCell } from './components/CommonTableCell/NumberCell';
import { get } from 'lodash-es';
import { getCountryFlag } from '@home/_config/countries';
import { getSystemFlag } from '@/pages/dashboard/pages/overview/_config/system';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { formatPercentNum, getUpAndDownStatus } from '@/pages/dashboard/utils/utils';
import { useDownloadDataSource } from './hooks/useDownloadDataSource';
import { useClientFilter } from './hooks/useClientFilter';
import { buildHoverCompareData, IHoverCompareBubbleConfig } from './utils/hoverCompareBubble';



const CommonTable = forwardRef<ICommonTableRef, ICommonTableProps>(
  (props, ref) => {
    const {
      className,
      style,
      data: dataProps,
      dataKey = "tableData",
      dataPath,
      totalRecordPath,
      clientFilter,
      metricsCardWrapProps,
      commonBaseTableProps,
      changeData,
      ...restProps
    } = props;

   
    const {data: apiData, errorMsg, loading: apiLoading} = dataProps?.[dataKey] || {};

    const {
      className: cardClassName,
      style: cardStyle,
      titleEn,
      titleCn,
      subtitleEn,
      subtitleCn,
      contentHeader,
      operation,
      ...restMetricsCardWrapProps
    } = metricsCardWrapProps || {};

    const {
      className: tableClassName,
      style: tableStyle,
      featConfig: featConfigProps,
      columns: columnsProps,
      componentMetrics,
      tableMetric: tableMetricProps,
      dataSource,
      tableConfig: tableConfigProps,
      isLoading: isLoadingProps,
      hasError,
      height,
      abnormalStyle,
      ...restCommonBaseTableProps
    } = commonBaseTableProps || {};

    const {
      pagination,
      onSortChange,
      hidePaginationWhenSinglePage,
      isShowPagination: isShowPaginationProp,
      hoverBubbleDateFormat,
      downloadSortKey,
      ...restTableConfigProps
    } = tableConfigProps || {};
    const { backendPaging, extraParamsKey, ...restPagination } = pagination || {};

    const { isEN } = useLocal();
    const { getFilterItemText } = useSelectorMap();
    const explainInfo = useSelector((state: any) => state?.dashboard?.explainInfo, (a, b) => a === b);
    const selectorMap = useSelector((state: any) => state?.dashboardSetting?.selectorMap, (a, b) => a === b);

    const data = dataSource || (dataPath ? get(apiData, dataPath) : apiData);
    const isLoading = isLoadingProps !== undefined ? isLoadingProps : apiLoading;
    const isError = hasError !== undefined ? hasError : errorMsg;

    const { activeData, clientFilteredCount } = useClientFilter(clientFilter, dataProps, data);

    const totalRecord =
      pagination?.total ??
      (clientFilter
        ? clientFilteredCount
        : (totalRecordPath ? get(apiData, totalRecordPath) : undefined)) ??
      activeData?.length ??
      0;
    const isNoData = useMemo(() => {
      return !(totalRecord > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalRecord, isLoading]);

    const visibleOperation = useVisibleOperation(operation, isNoData, isLoading, totalRecord);

    const isOpenForegroundPage = Boolean(
      get(restTableConfigProps, 'isOpenForegroundPage'),
    );
    const foregroundPaging = useForegroundPaging(isOpenForegroundPage, restPagination, activeData);

    const backendPagingResult = useBackendPaging(
      { backendPaging, extraParamsKey },
      changeData,
      dataProps?.extraParams,
      totalRecord,
    );

    const isShowPaginationComputed = useMemo(() => {
      if (isNoData) {
        return false;
      }
      if (hidePaginationWhenSinglePage) {
        const pageSize =
          backendPagingResult?.pageSize ??
          foregroundPaging?.pageSize ??
          restPagination.pageSize ??
          10;
        return totalRecord > pageSize;
      }
      /** 未开「单页隐藏」时：沿用调用方显式传入的 isShowPagination（如 changeAnalysis 等写死 false） */
      if (isShowPaginationProp !== undefined) {
        return isShowPaginationProp;
      }
      return true;
    }, [
      isNoData,
      hidePaginationWhenSinglePage,
      totalRecord,
      backendPagingResult?.pageSize,
      foregroundPaging?.pageSize,
      restPagination?.pageSize,
      isShowPaginationProp,
    ]);

    /** 目前仅支持 asc, desc, none 三种排序方式
     * TODO: 后续需要支持 asc_by_rate, desc_by_rate
     * * 注意区分 - CommonTable 这边使用的 ali-react-table [{ code: string, order: 'desc' | 'asc' | 'none' }]
     */
    const [sortState, setSortState] = useState<SortItem[]>([]);
    // 保存一份最新数据源，该数据源会受排序、分页等影响
    const [latestDataSource, setLatestDataSource] = useState<any[]>([]);
    const baseTableRef = useRef<ICommonBaseTableRef>(null);
    // 创建表格唯一 id，用于注册和注销表格
    const tableIdRef = useRef<string>(`table_${uuidv4()}`);

    /**
     * featConfig.sort.sorts：仅在该字段变化时同步（与改动前一致）。
     * 不可与 multi_sort / clientTableSort 放在同一 effect：否则请求返回后 extraParams 引用变化
     * 会反复执行 setSortState(默认 sorts)，覆盖用户列排序。
     */
    useEffect(() => {
      if (featConfigProps?.sort?.sorts) {
        setSortState(featConfigProps.sort.sorts || []);
      }
    }, [featConfigProps?.sort?.sorts]);

    /**
     * 无 featConfig.sort.sorts 时恢复列排序：
     * - 开启 backendPaging 时：multi_sort 与请求一致，可用 multi_sort ?? clientTableSort；
     * - 未开启 backendPaging：仅用 data.clientTableSort；不把 extraParams.multi_sort 放入依赖，
     *   否则每次请求后 extraParams 新引用会反复触发，易与纯前端排序、全屏后首帧不同步。
     * useLayoutEffect：在 paint 前对齐 sortState，减轻表头与 data.clientTableSort 一闪不一致。
     */
    useLayoutEffect(() => {
      if (featConfigProps?.sort?.sorts) {
        return;
      }
      const rawMulti = dataProps?.extraParams?.multi_sort;
      const rawClient = (dataProps as { clientTableSort?: unknown; })?.clientTableSort;
      const raw = backendPaging ? rawMulti ?? rawClient : rawClient;
      if (raw === undefined || raw === null) {
        return;
      }
      if (!Array.isArray(raw)) {
        return;
      }
      const multiSort = raw as Array<{ sort_by: string; desc: boolean; }>;
      if (!multiSort.length) {
        setSortState([]);
        return;
      }
      setSortState(convertSortFormat(multiSort, 'ali'));
    }, [
      featConfigProps?.sort?.sorts,
      backendPaging,
      backendPaging ? dataProps?.extraParams?.multi_sort : undefined,
      (dataProps as { clientTableSort?: unknown; })?.clientTableSort,
    ]);

    /** 通用：维度列「图标 + 文本」渲染 */
    const renderDimWithIcon = useCallback(
      (iconUrl: string | null, text: React.ReactNode, iconStyle: CSSProperties) => (
        <Cell>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {iconUrl && (
              <img
                src={iconUrl}
                style={iconStyle}
                alt=""
              />
            )}
            <span>{text}</span>
          </span>
        </Cell>
      ),
      [],
    );

    /** 列配置解析：优先 columns，否则从 componentMetrics + tableMetric 生成（兼容老逻辑） */
    const resolvedColumnsProps = useMemo(() => {
      if (columnsProps?.length) return resolveColumns(columnsProps, dataProps);
      if (!componentMetrics?.length) return [];
      // 优先用 commonBaseTableProps.tableMetric；PlatformCountryTableTransfer 场景下避免 data.tableMetric 覆盖导致 Gross/Net 列错乱
      const tableMetric = tableMetricProps !== null && tableMetricProps !== undefined ? tableMetricProps : dataProps?.tableMetric;
      const filtered = filterComponentMetricsByTableMetric(componentMetrics, tableMetric);
      return metricsToColumns(filtered, isEN, { explainInfo, metricsInfo: dataProps?.metricsInfo });
      /**
       * overrideWhen 函数只依赖 data.filter.is_confirm，超出此范围须同步更新 useMemo 依赖
       */
    }, [columnsProps, componentMetrics, tableMetricProps, dataProps?.tableMetric, isEN, explainInfo, dataProps?.filter?.is_confirm]);

    /** 前端下载用数据：列配置带 downloadConvertType 或 cell.type 的列会做 code→名称（仅下载前处理）
     * 前端分页时下载全量数据，否则用当前展示数据 */
    const dataForDownload = isOpenForegroundPage ? (activeData || []) : latestDataSource;
    const downloadDataSource = useDownloadDataSource(
      dataForDownload,
      resolvedColumnsProps || [],
      getFilterItemText,
    );

    /** 下载用列配置：在调用 download 前展开 DoD 列，使 download 工具保持通用 */
    const downloadColumns = useMemo(
      () => expandColumnsForDownload(resolvedColumnsProps || [], isEN),
      [resolvedColumnsProps, isEN],
    );

    // 对父组件暴露最新 dataSource，用于在分页、排序等影响下，获取最新的数据源
    useImperativeHandle(ref, () => ({
      get dataSource() {
        // 直接读取 pipeline 的实时状态
        return baseTableRef?.current?.pipeline?.getProps()?.dataSource || [];
      },
    }));

    useEffect(() => {
      setLatestDataSource(baseTableRef?.current?.pipeline?.getProps()?.dataSource || []);
    }, [activeData, dataSource, sortState]);

    // 注册和注销表格，使用 changeData 来传递表格信息给其子级
    useEffect(() => {
      if (!changeData) {
        return;
      }

      const tableId = tableIdRef.current;
      const tableInfo = {
        id: tableId,
        titleEn,
        titleCn,
        columns: resolvedColumnsProps || [],
        dataSource: latestDataSource || [],
      };

      changeData('__registerTable__', tableInfo);

      return () => {
        changeData('__unregisterTable__', tableId);
      };
    }, [titleEn, titleCn, resolvedColumnsProps, latestDataSource]);

    /**
     * 通用插槽渲染函数
     * @param content - 插槽内容，可以是 pageConfig 数组或 React 组件
     * @param className - 包裹容器的类名
     * @param keyPrefix - 用于生成 key 的前缀（可选）
     * @returns 渲染的插槽内容或 null
     */
    const renderSlot = (
      content: any | React.ReactNode | undefined,
      className?: string,
      keyPrefix?: string,
      restParams?: Object,
    ): React.ReactNode => {
      if (!content) return null;

      return (
        <div className={className}>
          {
            // 如果 content 是数组，表示它是从 pageConfig 中传入的
            Array.isArray(content)
              // 遍历数组，渲染每个配置项
              ? content.map((item, index: number) => (
                <Render
                  key={keyPrefix ? `${keyPrefix}_${item.name}_${index}` : `${item.name}_${index}`}
                  config={item}
                  tableColumns={keyPrefix === 'operation' ? downloadColumns : resolvedColumnsProps}
                  tableTitle={getName(isEN, titleEn, titleCn)}
                  tableData={keyPrefix === 'operation' ? downloadDataSource : latestDataSource}
                  data={props.data}
                  changeData={changeData}
                  {...restProps}
                  {...restParams}
                />
              ))
              // 表示 content 是从其他 TSX 文件中传入，是传统组件，直接渲染
              : content
          }
        </div>
      );
    };

    // 排序变化处理函数
    /** TODO: 这边的 SortBy 是基于 PerformanceMetricsTable 组件的 SortBy 类型，
     *  不会传 tea 组件默认的 default，而是传 undefined，后续需要转换为 ali-react-table 的 SortItem 类型
     * * 注意区分 - CommonTable 这边使用的 ali-react-table [{ code: string, order: 'desc' | 'asc' | 'none' }]
     * * 注意区分 - SortPopover 那边使用的 [{ by: string, order: 'desc' | 'asc' | 'undefined' }]
     */
    const handleSortChange = useCallback((sorts: SortItem[]) => {
      setSortState(sorts);
      if (backendPagingResult) {
        backendPagingResult.onSortChange(sorts);
      }
      onSortChange?.(convertSortFormat(sorts, 'popover'));
      // 如果配置了 downloadSortKey，把排序状态写入 data，供下载时读取
      if (downloadSortKey && changeData) {
        changeData(downloadSortKey, sorts?.length > 0 ? convertSortFormat(sorts, 'popover') : null);
      }
    }, [onSortChange, backendPagingResult, downloadSortKey, changeData]);

    const featConfig = useMemo(() => ({
      ...featConfigProps,
      // 默认开启列宽调整
      ...(!featConfigProps?.columnResize ? {
        columnResize: {
          minSize: 150,
        },
      } : {}),
      ...(!featConfigProps?.sort?.SortHeaderCell && featConfigProps?.sortable !== false ? {
        sort: {
          ...featConfigProps?.sort,
          SortHeaderCell: (props: SortHeaderCellProps) => <CommonTableHeader sortHeaderCellProps={props} onSortChange={handleSortChange} />,
          // 将 SortPopover 的 sorts 状态同步到 featConfig 中
          sorts: sortState,
          keepDataSource: false,
        },
      } : {}),
    }), [featConfigProps, sortState, handleSortChange]);

    const columns = useMemo(() => {
      const processColumn = (column: IColumn): IColumn => {
        const { features, renderHeader, children, getValue: originalGetValue, render: originalRender, title: originalTitle } = column;
        const { sortable, ...restFeatures } = features || {};

        const processedChildren = children?.map((childCol) => processColumn(childCol as IColumn));

        return {
          ...column,
          name: (isEN ? column?.nameEn : column?.nameCn) ?? column?.name ?? '',
          ...(renderHeader ? { renderHeader } : {}),
          ...(processedChildren ? { children: processedChildren } : {}),

          /** 为了排序功能，需要格式化原始值，但是不能在这边使用 dataFormat，它会返回 string | number，影响排序功能 */
          getValue: originalGetValue || ((record: any, _rowIndex: number) => {
            const rawValue = record[column?.code || ''];

            /** 如果值是字符串，且可以转换为数字，则转换为数字，后续用 number 来进行排序而不是 string */
            if (typeof rawValue === 'string') {
              const n = numeral(rawValue).value();
              if (typeof n === 'number' && Number.isFinite(n)) {
                return n;
              }
            }

            return rawValue;
          }),

          render: (value: any, record: Record<string, any>, rowIndex: number) => {
            const cellConfig = column?.features?.cell || {};
            const wrapWithRawValueBubble = (node: React.ReactNode) => {
              const {
                showRawOnHover = false,
                hoverValueKey,
                hoverFormatRule,
                hoverEmptyValue = '-',
                hoverLabelEn,
                hoverLabelCn,
                hoverBubble,
              } = cellConfig as {
                showRawOnHover?: boolean;
                hoverValueKey?: string;
                hoverFormatRule?: string;
                hoverEmptyValue?: string;
                hoverLabelEn?: string;
                hoverLabelCn?: string;
                hoverBubble?: boolean | (IHoverCompareBubbleConfig & {
                  enabled?: boolean;
                  type?: 'compare';
                  titleEn?: string;
                  titleCn?: string;
                  compareLabel?: string;
                });
              };

              if (!showRawOnHover) {
                return node;
              }

              const rawValue =
                hoverValueKey && hoverValueKey in record
                  ? record[hoverValueKey]
                  : record[column?.code || ''];
              let formattedRawValue = rawValue ?? hoverEmptyValue;
              if (hoverFormatRule) {
                formattedRawValue = dataCommonFormat({
                  value: rawValue,
                  formatRule: hoverFormatRule,
                  emptyValue: hoverEmptyValue,
                });
              }
              const hoverLabel = getName(isEN, hoverLabelEn, hoverLabelCn);
              const content = hoverLabel
                ? `${hoverLabel}: ${formattedRawValue}`
                : String(formattedRawValue ?? hoverEmptyValue);
              const hoverBubbleConfig = typeof hoverBubble === 'object' ? hoverBubble : undefined;
              if (hoverBubble === true || (hoverBubbleConfig?.enabled && hoverBubbleConfig.type === 'compare')) {
                const compareData = buildHoverCompareData({
                  record,
                  columnCode: column?.code,
                  emptyValue: hoverEmptyValue,
                  config: {
                    ...cellConfig,
                    ...(hoverBubbleDateFormat ? { dateFormat: hoverBubbleDateFormat } : {}),
                    ...hoverBubbleConfig,
                    timeType: get(dataProps, 'filter.timeType'),
                    dateType: get(dataProps, 'filter.date_type'),
                  },
                });
                const bubbleContent = (
                  <div className="dbd-metric-card-dod-bubble-item">
                    <div className="dbd-metric-card-dod-bubble-title">
                      {getName(isEN, compareData.titleEn, compareData.titleCn) || getName(isEN, hoverBubbleConfig?.titleEn, hoverBubbleConfig?.titleCn) || hoverLabel || 'MoM'}
                    </div>
                    <div className="dbd-metric-card-dod-bubble-flex">
                      <div className="dbd-metric-card-dod-bubble-text">
                        <div className="dbd-metric-card-dod-bubble-t">{compareData.currentDate || '-'}</div>
                        <div className="dbd-metric-card-dod-bubble-b">{compareData.compareDate || '-'}</div>
                      </div>
                      <div className="dbd-metric-card-dod-bubble-ct">{hoverBubbleConfig?.compareLabel || 'VS'}</div>
                      <div className="dbd-metric-card-dod-bubble-text dbd-common-table__raw-hover-bubble-value">
                        <div className="dbd-metric-card-dod-bubble-t">{compareData.currentValue || '-'}</div>
                        <div className="dbd-metric-card-dod-bubble-b">{compareData.compareValue || '-'}</div>
                      </div>
                    </div>
                  </div>
                );
                return (
                  <CommonBubble
                    content={bubbleContent}
                    dark
                    placement="top"
                    trigger="hover"
                    overlayClassName="dbd-metric-card-dod-bubble dbd-common-table__raw-hover-bubble"
                  >
                    <span className="dbd-common-table__raw-hover">{node}</span>
                  </CommonBubble>
                );
              }
              return (
                <span title={content}>
                  {node}
                </span>
              );
            };

            /** 如果列配置了 render 函数，则直接返回 render 函数的结果，注意使用 Cell 组件包裹
             * 优先级：外部传入的 render > cell.type
            */
            if (originalRender) {
              return wrapWithRawValueBubble(
                originalRender(value, record, rowIndex),
              );
            }

            // 格式化不能在 getValue 进行，它会影响排序
            const formattedValue = dataCommonFormat({ value: record[column?.code || ''], formatRule: column?.features?.format });

            const cellType = cellConfig?.type;

            switch (cellType) {
              case 'date':
                return <DateCell {...{value, record, config: column?.features?.cell || {}}} />;
              case 'mapping': {
                const rawValue = record[column?.code || ''];
                const mappingConfig = column?.features?.cell?.valueMapping?.[String(rawValue)];

                if (mappingConfig) {
                  const label = isEN ? mappingConfig.label : mappingConfig.labelCn;
                  const { status } = mappingConfig;

                  if (status) {
                    return wrapWithRawValueBubble(
                      <Cell>
                        <MetricAndUpDown
                          metric={undefined}
                          className={"dbd-common-table__metrics-mapping-up-down"}
                          updown={{ data: label, status }}
                          hasTooltip={false}
                        />
                      </Cell>,
                    );
                  }
                  return wrapWithRawValueBubble(<Cell>{label}</Cell>);
                }
                return wrapWithRawValueBubble(<Cell>{formattedValue}</Cell>);
              }
              case 'markdownLink':
                return wrapWithRawValueBubble(
                  <MarkdownLinkCell
                    {...{value, record, rowIndex, config: cellConfig || {}}}
                  />,
                );
              case 'trend':
              case 'percentage':
                return wrapWithRawValueBubble(
                  <PercentageCell
                    {...{
                      value,
                      formattedValue,
                      record,
                      rowIndex,
                      config: {...cellConfig || {}, column},
                    }}
                  />,
                );
              case 'country': {
                // 国家列：优先用行内名称字段（与 GameCell.countryNameKey 一致），否则用 code 走 getFilterItemText
                const countryCode = record?.countryCode || record?.country || record?.country_code;
                const flagUrl = countryCode ? getCountryFlag(countryCode) : null;
                const nameKey = (cellConfig as Record<string, unknown> | undefined)
                  ?.countryNameKey as string | undefined;
                const rawName = nameKey ? get(record, nameKey) : undefined;
                const nameFromRecord =
                  rawName !== null && rawName !== undefined && String(rawName).trim() !== ''
                    ? String(rawName).trim()
                    : '';
                let displayName = '-';
                if (nameFromRecord !== '') {
                  displayName = nameFromRecord;
                } else if (countryCode) {
                  displayName = getFilterItemText(
                    'country',
                    String(countryCode),
                    String(countryCode),
                  );
                }
                const dimContent = renderDimWithIcon(flagUrl, displayName, {
                  width: '24px',
                  height: '18px',
                  flexShrink: 0,
                });
                if (record?.jumpUrl) {
                  return wrapWithRawValueBubble(
                    <span
                      style={{ cursor: 'pointer'}}
                      onClick={() => window.open(record.jumpUrl)}
                    >
                      {dimContent}
                    </span>,
                  );
                }
                return wrapWithRawValueBubble(dimContent);
              }
              case 'system': {
                // 平台列：后端返回 code，通过 getFilterItemText 转为展示名称（与 CountryRegionRowItem 等用法一致）
                const systemCode = record?.[column?.code || 'system'];
                // 与 system 筛选器下拉一致：按英文名称（os_name_en）取图标，见 dashboardSetting getSelector / filterListMap nameCnMap.system
                const systemNameEn = systemCode && selectorMap?.nameCnMap?.system?.[systemCode]?.en;
                const rawKey = systemNameEn ?? systemCode ?? '';
                const cleanedSystemKey = rawKey ? String(rawKey).toLowerCase().replace(/\s+/g, '') : '';
                const systemIcon = cleanedSystemKey ? getSystemFlag(cleanedSystemKey) : '';
                const displayName = systemCode ? getFilterItemText('system', String(systemCode), String(systemCode)) : '-';
                return wrapWithRawValueBubble(
                  renderDimWithIcon(systemIcon, displayName, {
                    width: '20px',
                    height: '20px',
                    flexShrink: 0,
                  }),
                );
              }
              case 'gameIcon':
                return wrapWithRawValueBubble(
                  <GameCell
                    record={record}
                    config={Object.assign({}, cellConfig ?? {}, { column })}
                    getFilterItemText={getFilterItemText}
                  />,
                );
              case 'studioIcon':
                return wrapWithRawValueBubble(
                  <StudioCell
                    record={record}
                    config={Object.assign({}, cellConfig ?? {}, { column })}
                  />,
                );
              case 'sparkline': {
                const sparklineConfig = Object.assign({}, cellConfig ?? {}, { column });
                const hoverBubbleConfig = (cellConfig as { hoverBubble?: boolean | { enabled?: boolean; type?: string; }; })?.hoverBubble;
                const isCompareHoverBubble = hoverBubbleConfig === true || (typeof hoverBubbleConfig === 'object' && hoverBubbleConfig?.enabled && hoverBubbleConfig.type === 'compare');
                if (isCompareHoverBubble) {
                  return (
                    <SparklineCell
                      value={value}
                      formattedValue={String(formattedValue ?? '')}
                      record={record}
                      config={Object.assign(sparklineConfig, {
                        metricBubble: wrapWithRawValueBubble,
                      })}
                    />
                  );
                }
                return wrapWithRawValueBubble(
                  <SparklineCell
                    value={value}
                    formattedValue={String(formattedValue ?? '')}
                    record={record}
                    config={sparklineConfig}
                  />,
                );
              }
              case 'category':
                return wrapWithRawValueBubble(
                  <CategoryCell
                    value={record[column?.code || '']}
                    record={record}
                    config={Object.assign({}, cellConfig ?? {}, { column })}
                  />,
                );
              default:
            }

            /** 如果列配置了百分比格式化规则，则使用 PercentageCell 组件渲染 */
            if (column?.features?.format?.includes('percent') && !column?.render) {
              return wrapWithRawValueBubble(
                <PercentageCell {...{value, formattedValue, record, rowIndex, config: column?.features?.cell || {}}} />
              );
            }

            /** 数值列支持 DoD：当配置 showChangeRate + changeKey 时使用 MetricAndUpDown 展示主值 + 环比 */
            const { showChangeRate, changeKey, dodDecimals = 1 } = cellConfig as { showChangeRate?: boolean; changeKey?: string; dodDecimals?: number; };
            // eslint-disable-next-line semi
            if (showChangeRate && changeKey) {
              const dodRaw = get(record, changeKey);
              // 参考 OverallTableCo：无数据时 formatPercentNum 返回 '-'
              const dodFormatted = formatPercentNum(parseFloat(String(dodRaw ?? '')), dodDecimals, true);
              const dodStatus = getUpAndDownStatus(dodRaw);
              return wrapWithRawValueBubble(
                <NumberCell config={cellConfig}>
                  <MetricAndUpDown
                    metric={formattedValue}
                    updown={{ data: dodFormatted, status: dodStatus }}
                    hasTooltip={false}
                  />
                </NumberCell>,
              );
            }

            if (Number.isFinite(value)) {
              return wrapWithRawValueBubble(
                <NumberCell config={cellConfig}>
                  {formattedValue}
                </NumberCell>,
              );
            }

            return wrapWithRawValueBubble(
              <Cell>
                {formattedValue}
              </Cell>,
            );
          },

          title: (
            renderSlot(originalTitle, 'dbd-common-table__header', 'tableHeader', {
              column,
            })
          ),

          features: {
            sortable:
              // 默认开启排序
              features?.sortable === undefined || features?.sortable === true
                ? (a: any, b: any) => defaultCompareFn(a, b, sortState?.[0]?.order === 'desc')
                : features?.sortable,
            ...restFeatures,
          },
        };
      };

      return resolvedColumnsProps?.map(processColumn) ?? [];
    }, [resolvedColumnsProps, isEN, sortState, getFilterItemText, renderDimWithIcon, hoverBubbleDateFormat]);

    return (
      <div
        className={classNames('dbd-common-table', className)}
        style={style}
      >
        {/* 表头 */}
        <MetricsCardWrap
          titleEn={titleEn}
          titleCn={titleCn}
          subtitleEn={subtitleEn}
          subtitleCn={subtitleCn}
          className={classNames('dbd-common-table__metrics-card-wrap', cardClassName,)}
          style={cardStyle}
          contentHeader={renderSlot(contentHeader, 'dbd-common-table__metrics-card-wrap__content-header', 'contentHeader')}
          operation={renderSlot(visibleOperation, 'dbd-common-table__metrics-card-wrap__operation', 'operation')}
          {...restMetricsCardWrapProps}
        >
          <Abnormal
            isLoading={false}
            componentLoading={isLoading}
            isError={isError}
            isNoData={isNoData}
            style={{height, ...abnormalStyle}}
            component={
              // 表格
              // tableConfig 里面的 primaryKey 为必传项，若不传，会有性能问题，比如表格拖拽时卡顿问题
              <CommonBaseTable
                ref={baseTableRef}
                className={classNames('dbd-common-table__content', tableClassName)}
                style={tableStyle}
                columns={columns}
                featConfig={featConfig}
                tableConfig={{
                  pagination: {
                    total: totalRecord ?? 0,
                    pageSizeOptions: PAGE_SIZE_OPTIONS,
                    ...restPagination,
                    ...foregroundPaging,
                    ...backendPagingResult,
                  },
                  ...restTableConfigProps,
                  isShowPagination: isShowPaginationComputed,
                }}
                dataSource={activeData || []}
                {...restCommonBaseTableProps}
              />
            }
          />
        </MetricsCardWrap>
      </div>
    );
  },
);

CommonTable.displayName = 'CommonTable';

export default CommonTable;